import { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from '@mui/material';

import { useUpsertRule } from '../../hooks';
import { RULE_TYPE_LABELS, RULE_TYPE_OPTIONS } from '../../model/labels';
import type { LoyaltyRule, LoyaltyRuleType } from '../../model/types';

interface RuleFormDialogProps {
  open: boolean;
  /** The rule being edited, or `null` to create a new one. */
  rule: LoyaltyRule | null;
  onClose: () => void;
  onDone: (message: string) => void;
}

interface ParseResult {
  ok: boolean;
  value?: Record<string, unknown>;
}

/** Parse the params JSON textarea — must be a JSON object (or blank → `{}`). */
function parseParams(text: string): ParseResult {
  const trimmed = text.trim();
  if (!trimmed) return { ok: true, value: {} };
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return { ok: true, value: parsed as Record<string, unknown> };
    }
    return { ok: false };
  } catch {
    return { ok: false };
  }
}

/** Create / update a loyalty rule (upsert by code; gated by `loyalty.manage`). */
export function RuleFormDialog({ open, rule, onClose, onDone }: RuleFormDialogProps) {
  const editing = rule !== null;
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<LoyaltyRuleType>('ACCRUAL');
  const [enabled, setEnabled] = useState(true);
  const [paramsText, setParamsText] = useState('{}');
  const [error, setError] = useState<string | null>(null);
  const upsert = useUpsertRule();

  useEffect(() => {
    if (open) {
      setCode(rule?.code ?? '');
      setName(rule?.name ?? '');
      setType(rule?.type ?? 'ACCRUAL');
      setEnabled(rule?.enabled ?? true);
      setParamsText(rule?.params ? JSON.stringify(rule.params, null, 2) : '{}');
      setError(null);
    }
  }, [open, rule]);

  const params = parseParams(paramsText);
  const valid = code.trim().length > 0 && name.trim().length > 0 && params.ok;

  const submit = () => {
    setError(null);
    upsert.mutate(
      {
        code: code.trim(),
        name: name.trim(),
        type,
        enabled,
        params: params.value,
      },
      {
        onSuccess: (saved) => {
          onDone(`Правило «${saved.name}» сохранено.`);
          onClose();
        },
        onError: (err) => setError(err.message),
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{editing ? 'Изменить правило' : 'Новое правило'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label="Код"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            fullWidth
            disabled={editing}
            autoFocus={!editing}
            slotProps={{ htmlInput: { maxLength: 64 } }}
            helperText={editing ? 'Код нельзя изменить (обновление по коду).' : 'Напр. BASE_ACCRUAL'}
          />
          <TextField
            label="Название"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 255 } }}
          />
          <TextField
            select
            label="Тип"
            value={type}
            onChange={(e) => setType(e.target.value as LoyaltyRuleType)}
            fullWidth
          >
            {RULE_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {RULE_TYPE_LABELS[option]}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
            label="Включено"
          />
          <TextField
            label="Параметры (JSON)"
            value={paramsText}
            onChange={(e) => setParamsText(e.target.value)}
            error={!params.ok}
            helperText={
              params.ok
                ? 'Параметры правила в виде JSON-объекта (напр. { "pointsPerCurrencyUnit": 1 }).'
                : 'Некорректный JSON-объект.'
            }
            fullWidth
            multiline
            minRows={4}
            slotProps={{ htmlInput: { style: { fontFamily: 'monospace' } } }}
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={upsert.isPending}>
          Отмена
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={upsert.isPending || !valid}
          startIcon={upsert.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}
